/**
 * Resolve a node's outer width/height for the layout solver.
 *
 * Resolution policy: **the widget owns its size.** The solver does NOT
 * read `widget.width` / `widget.height` from the instance directly;
 * those are just like any other `widget.*` prop and are forwarded to
 * the widget as kebab-cased attrs. The widget decides whether to honor
 * them. This gives widgets the choice between:
 *
 *   - **Customizable** — widget's `sizeFor(props, ctx)` reads
 *     `props.get("width")` / `props.get("height")` and falls back to
 *     a default when absent. (The default `arch-module` does this.)
 *   - **Fixed** — widget exposes `static archWidth` / `archHeight`
 *     and/or returns a constant from `sizeFor`, ignoring the props.
 *     `widget.width: 999` on the instance has no effect.
 *   - **Computed** — widget derives size from the model
 *     (e.g. `arch-db-table` returns `headerH + rowCount * rowH`).
 *
 * Resolution order (per axis):
 *   1. Widget tag `sizeFor(props, ctx)`: pure, synchronous,
 *      deterministic — same inputs always yield the same size. Errors
 *      and non-numeric returns are ignored.
 *   2. Tag default: `static archWidth` / `static archHeight` on the
 *      registered custom-element constructor
 *   3. Zero — solver treats this as "use NODE_W / NODE_H defaults"
 *
 * Returning zero matches the existing convention in stressLayout.ts:
 *     w: n.w > 0 ? n.w : NODE_W
 * so callers don't need a separate "use defaults" sentinel.
 *
 * Reads `customElements` synchronously. `main.tsx` awaits widget bundle
 * load before first render, so by the time layout runs every declared
 * tag is registered. Tags never registered (typo in `.arch`, bundle
 * failed to load) silently fall through to zero.
 */
import type { Interface, AspectEntry, Module } from "../arch-model/model.js";
import type { WidgetPropValue, WidgetSpec } from "../arch-model/widget-spec.js";
import { noteCardSize } from "../shapes.js";
import { FIGURE_SPECS, figureCaptionBand } from "../figures.js";

/**
 * Context passed to `sizeFor`. A minimal projection of the module so
 * widgets can derive size from structural facts (interface count, label
 * presence) without coupling to the full Module type and its churn.
 */
export interface SizeForContext {
  readonly typeName?: string;
  readonly interfaces: readonly Interface[];
  readonly aspects: readonly AspectEntry[];
  /** The module's bare-string description. Lets text-bodied widgets
   *  (e.g. `arch-note`) size their height to the content length up
   *  front, without DOM measurement. Absent when the module has none. */
  readonly description?: string;
}

/**
 * Size metadata a widget exposes for the layout solver — the pure subset
 * of a widget needed to size its node BEFORE render. Browser-side this is
 * the custom-element constructor; server-side it's a plain object from the
 * scene-widget catalog. Decoupling it from `customElements` lets the whole
 * graph→layout pipeline run headless.
 */
export interface WidgetSizeEntry {
  readonly archWidth?: unknown;
  readonly archHeight?: unknown;
  readonly sizeFor?: (
    props: ReadonlyMap<string, WidgetPropValue>,
    ctx: SizeForContext,
  ) => unknown;
  readonly rowAnchorFrac?: (
    interfaceName: string,
    ctx: SizeForContext,
  ) => unknown;
}

/** Resolves a widget tag to its size metadata. */
export interface WidgetSizeRegistry {
  get(tag: string): WidgetSizeEntry | undefined;
}

// Default registry: read the live custom-element constructor when a DOM is
// present (browser), nothing otherwise (server). The browser/board layer or
// a headless caller can override via `setWidgetSizeRegistry`.
export const customElementsRegistry: WidgetSizeRegistry = {
  get(tag) {
    if (typeof customElements === "undefined") return undefined;
    return customElements.get(tag) as WidgetSizeEntry | undefined;
  },
};

let activeRegistry: WidgetSizeRegistry = customElementsRegistry;

/** Inject the widget-size source — a scene-widget catalog server-side, the
 *  custom-element registry browser-side (the default). */
export function setWidgetSizeRegistry(registry: WidgetSizeRegistry): void {
  activeRegistry = registry;
}

/**
 * Headless default registry: every tag honors explicit `widget.width` /
 * `widget.height` props and nothing else, so unsized nodes fall through to
 * the solver's NODE_W / NODE_H defaults. Mirrors the live scene widget's
 * `sizeFor`, keeping browser and headless board geometry identical. The one
 * shared implementation for server / MCP / tests — do not re-hardcode dims.
 */
export const explicitSizeRegistry: WidgetSizeRegistry = {
  get: () => EXPLICIT_ENTRY,
};

const numProp = (v: unknown): number | undefined => {
  const n =
    typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

const EXPLICIT_ENTRY: WidgetSizeEntry = {
  sizeFor: (props) => ({ w: numProp(props.get("width")), h: numProp(props.get("height")) }),
};

/**
 * arch-note grows to fit its description. Height comes from the scene text
 * metrics (`noteCardSize`) so the node is always exactly tall enough — no clip
 * — and the live board + headless export share this ONE computation, keeping
 * their geometry byte-identical. `widget.width` fixes the column width;
 * `widget.height`, if set, acts as a MINIMUM the note grows up from.
 */
const NOTE_ENTRY: WidgetSizeEntry = {
  sizeFor: (props, ctx) => {
    const width = numProp(props.get("width"));
    const { w, h } = noteCardSize({ name: "", description: ctx.description }, width);
    const floor = numProp(props.get("height"));
    return { w, h: floor != null ? Math.max(h, floor) : h };
  },
};

/**
 * Device/actor figures: the drawing's designed dims PLUS the caption band —
 * the name line under the glyph. The stdlib elements' archWidth/archHeight
 * describe the DRAWING alone; sizing the node to that leaves the caption no
 * room (the HTML backend floors the svg at its aspect-transferred height and
 * crushes the name to nothing). Explicit `widget.width`/`height` still win.
 */
const figureEntries = new Map<string, WidgetSizeEntry>();
function figureEntry(tag: string): WidgetSizeEntry {
  let entry = figureEntries.get(tag);
  if (!entry) {
    entry = {
      sizeFor: (props) => {
        const spec = FIGURE_SPECS[tag]!;
        return {
          w: numProp(props.get("width")) ?? spec.w,
          h: numProp(props.get("height")) ?? spec.h + figureCaptionBand(),
        };
      },
    };
    figureEntries.set(tag, entry);
  }
  return entry;
}

/**
 * Size registry that sizes the scene-composed diagram shapes via the shared
 * scene metrics — `arch-note` grows to its text, the device/actor figures
 * reserve their caption band — delegating every other tag to `base`. Both
 * the headless export (`base` = explicit width/height only) and the live
 * board (`base` = the custom-element registry) wrap this, so these shapes
 * size identically on either backend while modules/tables keep their own
 * sizing.
 */
export function shapeSizeRegistry(base: WidgetSizeRegistry): WidgetSizeRegistry {
  return {
    get: (tag) =>
      tag === "arch-note" ? NOTE_ENTRY : FIGURE_SPECS[tag] ? figureEntry(tag) : base.get(tag),
  };
}

export function widgetTagSize(
  tag: string | undefined,
  props: ReadonlyMap<string, WidgetPropValue> = EMPTY_PROPS,
  ctx: SizeForContext = EMPTY_CTX,
): { w?: number; h?: number } {
  if (!tag) return {};
  const ctor = activeRegistry.get(tag);
  if (!ctor) return {};
  const out: { w?: number; h?: number } = {};
  if (typeof ctor.archWidth === "number") out.w = ctor.archWidth;
  if (typeof ctor.archHeight === "number") out.h = ctor.archHeight;
  if (typeof ctor.sizeFor === "function") {
    let dyn: unknown;
    try {
      dyn = ctor.sizeFor(props, ctx);
    } catch {
      // Pure-function contract violated; treat as no dynamic size.
      dyn = undefined;
    }
    if (dyn && typeof dyn === "object") {
      const d = dyn as { w?: unknown; h?: unknown };
      if (typeof d.w === "number" && Number.isFinite(d.w)) out.w = d.w;
      if (typeof d.h === "number" && Number.isFinite(d.h)) out.h = d.h;
    }
  }
  return out;
}

/**
 * Vertical anchor fraction (0..1) for the row a widget renders for the
 * named interface, or undefined when the widget doesn't do row anchoring
 * (no `rowAnchorFrac` hook, or the hook returns null for this interface).
 * Same ctor-resolution + pure-function contract as `widgetTagSize`.
 */
export function widgetTagRowAnchor(
  tag: string | undefined,
  interfaceName: string,
  ctx: SizeForContext = EMPTY_CTX,
): number | undefined {
  if (!tag) return undefined;
  const ctor = activeRegistry.get(tag);
  if (!ctor || typeof ctor.rowAnchorFrac !== "function") return undefined;
  let frac: unknown;
  try {
    frac = ctor.rowAnchorFrac(interfaceName, ctx);
  } catch {
    return undefined;
  }
  return typeof frac === "number" && Number.isFinite(frac) ? frac : undefined;
}

export function resolveNodeSize(
  spec: WidgetSpec,
  module?: Pick<Module, "typeName" | "interfaces" | "aspects" | "description">,
): { w: number; h: number } {
  const ctx: SizeForContext = module
    ? {
        typeName: module.typeName,
        interfaces: module.interfaces,
        aspects: module.aspects,
        description: module.description,
      }
    : EMPTY_CTX;
  const tagSize = widgetTagSize(spec.tag, spec.props, ctx);
  return { w: tagSize.w ?? 0, h: tagSize.h ?? 0 };
}

const EMPTY_PROPS: ReadonlyMap<string, WidgetPropValue> = new Map();
const EMPTY_CTX: SizeForContext = { interfaces: [], aspects: [] };
